import { registerLocaleData } from '@angular/common';
import { DOCUMENT, Inject, Injectable } from '@angular/core';
import { loadTranslations } from '@angular/localize';
import { Params, Router } from '@angular/router';
import { AssessmentUserApiService } from '@app/assessments/services/assessment-user-api.service';
import { UserPreferredLanguage } from '@app/launchers/models/user-preferred-language';
import { ApiResponse } from '@app/shared/models/api-response';
import { Constants } from '@app/shared/models/constants';
import { Language } from '@app/shared/models/language';
import { StorageService } from '@app/shared/services/storage.service';
import { TranslateService } from '@app/shared/translate/translate.service';
import { LangUtils } from '@app/shared/utilities/lang.utils';
import { UrlUtils } from '@app/shared/utilities/url.utils';
import { lastValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class I18nService {

	#defaultLocale = 'en-GB';
	locale = this.#defaultLocale;

	constructor (
		private readonly storageService: StorageService,
		@Inject(DOCUMENT) private readonly document: Document,
		private readonly assessmentUserApiService: AssessmentUserApiService,
		private readonly router: Router,
		private readonly translateService: TranslateService
	) { }

	async setup (): Promise<void> {
		try {

			// Get query params from URL using native JS
			let queryParams = UrlUtils.extractQueryParamsFromUrl(this.document.location.search);
			let userAssessmentId = this.getUserAssessmentId(queryParams);

			if (queryParams.source === 'email' && userAssessmentId) {
				await this.replaceUrlWithoutSource(queryParams);
				await this.setAngularLocaleByUserAssessmentId(userAssessmentId);
			} else {
				this.locale = this.getAngularLocale();
			}

			$localize.locale = this.locale;

			// It is not necessary to load any translations for the default locale.
			if (this.locale === this.#defaultLocale) {
				this.setDocumentI18nAttributes();
				return;
			}

			await this.getAndRegisterLocale();
			await this.getAndLoadTranslations();

			this.setDocumentI18nAttributes();

		} catch (error) {
			console.error('Error setting locale:', error);
		}
	}

	private setDocumentI18nAttributes () {
		this.setDocumentLanguage();
		this.setDocumentDirection();

	}

	private setDocumentDirection (): void {
		const isRtl = LangUtils.isRtlLocale(this.locale);
		const direction = isRtl ? 'rtl' : 'ltr';

		this.document.documentElement.dir = direction;
	}

	private setDocumentLanguage () {
		this.document.documentElement.lang = this.locale;
	}

	private async setAngularLocaleByUserAssessmentId (userAssessmentId: string) {
		const { records } = await this.getUserPreferredLanguageByUserAssessmentId(userAssessmentId);
		const language = this.translateService.getLanguageByCultureName(records.cultureName);
		this.translateService.setAppLanguage(language);
		this.locale = language.angularLocale;
	}

	private getUserAssessmentId (queryParams: any): string {
		const { userAssessmentId, returnUrl } = queryParams;

		if (!returnUrl) {
			return userAssessmentId;
		}

		const returnUrlQueryParams = UrlUtils.extractQueryParamsFromUrl(returnUrl);
		return returnUrlQueryParams.userAssessmentId || userAssessmentId;
	}

	private async replaceUrlWithoutSource (queryParams: Params): Promise<void> {
		const { source, ...rest } = queryParams;
		const newQueryParams = new URLSearchParams(rest).toString();
		const url = this.document.location.pathname + (newQueryParams ? `?${ newQueryParams }` : '');
		await this.router.navigateByUrl(url, { replaceUrl: true });
	}

	private async importLocaleModule (locale: string): Promise<{ default: any }> {
		return await import(
			`../../../../node_modules/@angular/common/locales/${ locale }.js`
		);
	}

	private async getAndRegisterLocale () {
		let localeModule: { default: any };
		try {
			localeModule = await this.importLocaleModule(this.locale);
		} catch {
			console.warn(`Locale ${ this.locale } not found, falling back to ${ this.#defaultLocale }`);
			try {
				localeModule = await this.importLocaleModule(this.#defaultLocale);
			} catch (error) {
				console.error('Error loading fallback locale:', error);
				return;
			}
		}
		registerLocaleData(localeModule.default);
	}

	private async importTranslationsModule (locale: string): Promise<{ default: any }> {
		return await import(`../../../i18n/messages.${ locale }.json`);
	}

	private async getAndLoadTranslations () {
		let translationsModule: { default: any };
		try {
			translationsModule = await this.importTranslationsModule(this.locale);
		} catch {
			// Loading nothing leaves the source text from the templates, which is the default locale
			console.warn(`Translations for locale ${ this.locale } not found, falling back to ${ this.#defaultLocale }`);
			return;
		}
		loadTranslations(translationsModule.default.translations);
	}

	private getAngularLocale (): string {
		const storedLanguageObject = this.storageService.getItem(Constants.LanguageStorageKey);

		if (!storedLanguageObject) {
			return this.#defaultLocale;
		}

		try {
			return (<Language>JSON.parse(storedLanguageObject)).angularLocale;
		} catch (error) {
			console.error('Error getting locale:', error);
			return this.#defaultLocale;
		}
	}

	private async getUserPreferredLanguageByUserAssessmentId (userAssessmentId: string): Promise<ApiResponse<UserPreferredLanguage>> {
		return lastValueFrom(this.assessmentUserApiService.getUserPreferredLanguageByUserAssessmentId(userAssessmentId));
	}
}
